import { useCallback, useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateStripePriceEntitlement,
  getAdminGetStripePriceEntitlementsQueryKey,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { updateStripePriceSchema, type UpdateStripePrice } from '@/schemas/stripe-price';
import { EntitlementRuleFields } from './EntitlementRuleFields';
import { logger } from '@/services/logger';
import type { EntitlementRule } from '@/api/auth/missionImpactAuthAPI.schemas';
import {
  BILLING_SCENARIOS,
  BILLING_SCENARIO_LABELS,
  type BillingScenario,
} from '@/lib/billing-scenarios';

// Sentinel value for the "no scenario" option (Radix Select disallows empty string values).
const NO_SCENARIO = 'none';

/**
 * Props for the EditStripePriceDialog component
 */
interface EditStripePriceDialogProps {
  /** The product ID */
  productId: string;
  /** The Stripe price ID to edit */
  stripePriceId: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Current entitlement rule */
  entitlementRule: EntitlementRule;
  /** Current adjustable quantity setting */
  adjustableQuantity: boolean;
  /** Current minimum quantity */
  minQuantity: number;
  /** Current maximum quantity */
  maxQuantity: number | null | undefined;
  /** Current billing scenario assigned to this price */
  scenario?: BillingScenario | null;
  /** Whether this price is the pre-selected default plan for its tier */
  isTierDefault?: boolean;
}

/**
 * Dialog component for editing an existing Stripe price mapping
 */
export function EditStripePriceDialog({
  productId,
  stripePriceId,
  open,
  onOpenChange,
  entitlementRule,
  adjustableQuantity: currentAdjustableQuantity,
  minQuantity: currentMinQuantity,
  maxQuantity: currentMaxQuantity,
  scenario: currentScenario = null,
  isTierDefault: currentIsTierDefault = false,
}: EditStripePriceDialogProps) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpdateStripePrice>({
    // The schema validates the generated polymorphic rule; cast bridges the resolver's input type.
    resolver: zodResolver(updateStripePriceSchema) as Resolver<UpdateStripePrice>,
    defaultValues: {
      entitlementRule,
      scenario: currentScenario,
      isTierDefault: currentIsTierDefault,
      adjustableQuantity: currentAdjustableQuantity,
      minQuantity: currentMinQuantity,
      maxQuantity: currentMaxQuantity ?? null,
    },
  });

  // Reset form when props change (e.g., opening with different price)
  useEffect(() => {
    if (open) {
      reset({
        entitlementRule,
        scenario: currentScenario,
        isTierDefault: currentIsTierDefault,
        adjustableQuantity: currentAdjustableQuantity,
        minQuantity: currentMinQuantity,
        maxQuantity: currentMaxQuantity ?? null,
      });
    }
  }, [open, entitlementRule, currentScenario, currentIsTierDefault, currentAdjustableQuantity, currentMinQuantity, currentMaxQuantity, reset]);

  const adjustableQuantity = watch('adjustableQuantity');
  const scenario = watch('scenario');
  const isTierDefault = watch('isTierDefault');

  const { mutate: updateStripePriceEntitlement } = useAdminUpdateStripePriceEntitlement({
    mutation: {
      onSuccess: () => {
        toast.success('Stripe price mapping updated successfully');

        // Invalidate queries to refresh the list
        queryClient.invalidateQueries({
          queryKey: getAdminGetStripePriceEntitlementsQueryKey(productId),
        });

        // Close dialog
        onOpenChange(false);
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'UpdateStripePriceEntitlement',
          productId,
          stripePriceId,
        });
        toast.error(
          'Failed to update Stripe price mapping. ' +
            (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const onSubmit = useCallback(
    (data: UpdateStripePrice) => {
      updateStripePriceEntitlement({
        productId,
        stripePriceId,
        data: {
          entitlementRule: data.entitlementRule,
          scenario: data.scenario ?? null,
          isTierDefault: data.isTierDefault,
          adjustableQuantity: data.adjustableQuantity,
          minQuantity: data.minQuantity,
          maxQuantity: data.maxQuantity,
        },
      });
    },
    [updateStripePriceEntitlement, productId, stripePriceId]
  );

  /**
   * Handle dialog close
   */
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isSubmitting) {
        reset();
      }
      onOpenChange(newOpen);
    },
    [isSubmitting, onOpenChange, reset]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Stripe Price</DialogTitle>
          <DialogDescription>
            Update the settings for{' '}
            <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
              {stripePriceId}
            </code>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <EntitlementRuleFields
            setValue={setValue}
            watch={watch}
            fieldPath="entitlementRule"
            error={errors.entitlementRule?.message}
          />

          <div className="grid gap-2">
            <Label htmlFor="scenario">Billing scenario</Label>
            <Select
              value={scenario ?? NO_SCENARIO}
              onValueChange={(value) =>
                setValue('scenario', value === NO_SCENARIO ? null : (value as BillingScenario))
              }
            >
              <SelectTrigger id="scenario">
                <SelectValue placeholder="No scenario (unassigned)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SCENARIO}>No scenario (unassigned)</SelectItem>
                {BILLING_SCENARIOS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {BILLING_SCENARIO_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              The tier offering this price serves. Determines which organizations are shown it.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isTierDefault"
              checked={isTierDefault}
              onCheckedChange={(checked) => setValue('isTierDefault', checked === true)}
            />
            <Label htmlFor="isTierDefault" className="text-sm font-medium leading-none">
              Pre-selected default plan for its tier
            </Label>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="adjustableQuantity"
                checked={adjustableQuantity}
                onCheckedChange={(checked) => setValue('adjustableQuantity', checked === true)}
              />
              <Label htmlFor="adjustableQuantity" className="text-sm font-medium leading-none">
                Allow adjustable quantity at checkout
              </Label>
            </div>

            {adjustableQuantity && (
              <div className="grid grid-cols-2 gap-4 pl-6">
                <div className="grid gap-2">
                  <Label htmlFor="minQuantity">Minimum quantity</Label>
                  <Input
                    id="minQuantity"
                    type="number"
                    min={0}
                    {...register('minQuantity', { valueAsNumber: true })}
                  />
                  {errors.minQuantity && (
                    <p className="text-sm text-destructive">{errors.minQuantity.message}</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="maxQuantity">Maximum quantity</Label>
                  <Input
                    id="maxQuantity"
                    type="number"
                    min={1}
                    placeholder="Unlimited"
                    {...register('maxQuantity', {
                      setValueAs: (v) => (v === '' || v === null ? null : parseInt(v, 10)),
                    })}
                  />
                  {errors.maxQuantity && (
                    <p className="text-sm text-destructive">{errors.maxQuantity.message}</p>
                  )}
                  <p className="text-sm text-muted-foreground">Leave blank for unlimited</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
